import { AppError } from "../../shared/errors/AppError";

const BYTES_PER_RGBA_PIXEL = 4;
const DEFAULT_MEMORY_BUDGET_BYTES = 512 * 1024 * 1024;
const DEFAULT_VECTOR_NODE_LIMIT = 250_000;

export type ExportMemoryEstimate = {
  width: number;
  height: number;
  pixels: number;
  estimatedBytes: number;
};

export function estimateRgbaMemory(width: number, height: number, surfaces = 1): ExportMemoryEstimate {
  const safeWidth = Math.max(1, Math.floor(width));
  const safeHeight = Math.max(1, Math.floor(height));
  const safeSurfaces = Math.max(1, Math.floor(surfaces));
  const pixels = safeWidth * safeHeight;
  return {
    width: safeWidth,
    height: safeHeight,
    pixels,
    estimatedBytes: pixels * BYTES_PER_RGBA_PIXEL * safeSurfaces,
  };
}

export function assertExportMemoryBudget(
  width: number,
  height: number,
  surfaces: number,
  budgetBytes = DEFAULT_MEMORY_BUDGET_BYTES,
) {
  const estimate = estimateRgbaMemory(width, height, surfaces);
  if (!Number.isSafeInteger(estimate.pixels) || estimate.estimatedBytes > budgetBytes) {
    const estimatedMb = Math.ceil(estimate.estimatedBytes / (1024 * 1024));
    const budgetMb = Math.floor(budgetBytes / (1024 * 1024));
    throw new AppError(
      "MEMORY_PRESSURE",
      `This export is estimated to require about ${estimatedMb} MB of working image memory, above the ${budgetMb} MB safety budget. Reduce resolution or export scale.`,
    );
  }
  return estimate;
}

export function estimateHalftoneVectorNodes(width: number, height: number, cellSize: number) {
  const cell = Math.max(2, Math.round(cellSize));
  return Math.ceil(width / cell) * Math.ceil(height / cell);
}

export function assertVectorComplexity(
  width: number,
  height: number,
  cellSize: number,
  maxNodes = DEFAULT_VECTOR_NODE_LIMIT,
) {
  const estimatedNodes = estimateHalftoneVectorNodes(width, height, cellSize);
  if (estimatedNodes > maxNodes) {
    throw new AppError(
      "VECTOR_COMPLEXITY",
      `This SVG would contain roughly ${estimatedNodes.toLocaleString()} vector dots, above the ${maxNodes.toLocaleString()} safety limit. Increase Halftone cell size or reduce the source dimensions.`,
    );
  }
  return estimatedNodes;
}
